import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import {
  Plus, Bot, Phone, PhoneIncoming, PhoneOutgoing,
  Activity, AlertCircle, TrendingUp, Clock, RefreshCw
} from 'lucide-react';

export default function DashboardPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [stats, setStats] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [agentsRes, billingRes, annRes] = await Promise.all([
        api.get('/agents'),
        api.get('/billing/overview'),
        api.get('/users/announcements'),
      ]);
      setAgents(agentsRes.data.agents);
      setStats(billingRes.data);
      setAnnouncements(annRes.data.announcements || []);
    } catch (err) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const syncWithTelnyx = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/agents/sync');
      const { upserted = 0, deleted = 0, unassigned = 0, errors = [] } = res.data;
      if (upserted > 0 || deleted > 0) {
        toast.success(`Sync: ${upserted} updated, ${deleted} removed${unassigned ? `, ${unassigned} unassigned` : ''}`);
        loadData();
      } else {
        toast.success(`Everything in sync${unassigned ? ` (${unassigned} unassigned in Telnyx)` : ''}`);
      }
      if (errors.length > 0) toast.error(`${errors.length} sync error(s)`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const demoExpiringSoon = user?.is_demo && user?.demo_expires
    && (user.demo_expires - Date.now() / 1000) < 7 * 24 * 60 * 60;

  if (loading) return <div className="page-loading"><div className="spinner" /></div>;

  return (
    <div className="page">
      {/* Announcements */}
      {announcements.map(a => (
        <div key={a.id} className="banner banner--info">
          <AlertCircle size={16} /> {a.message}
        </div>
      ))}

      {/* Demo warning */}
      {user?.is_demo === 1 && (
        <div className="banner banner--demo">
          <AlertCircle size={16} />
          <span>
            🎯 You're in Demo Mode with ${user?.balance?.toFixed(2)} credit.
            {demoExpiringSoon ? ' Your demo expires soon!' : ''}
            <Link to="/billing" className="banner-link">Upgrade your plan →</Link>
          </span>
        </div>
      )}

      {/* Stats row */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon stat-icon--blue"><Bot size={22} /></div>
          <div className="stat-info">
            <span className="stat-value">{agents.length}</span>
            <span className="stat-label">Active Agents</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon stat-icon--green"><Activity size={22} /></div>
          <div className="stat-info">
            <span className="stat-value">${user?.balance?.toFixed(2)}</span>
            <span className="stat-label">Balance</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon stat-icon--purple"><Clock size={22} /></div>
          <div className="stat-info">
            <span className="stat-value">{stats?.minutes_used?.toFixed(0) || 0}</span>
            <span className="stat-label">Minutes Used</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon stat-icon--orange"><TrendingUp size={22} /></div>
          <div className="stat-info">
            <span className="stat-value">{stats?.plan?.toUpperCase()}</span>
            <span className="stat-label">Current Plan</span>
          </div>
        </div>
      </div>

      {/* Agents section */}
      <div className="section">
        <div className="section-header">
          <h2>Your AI Voice Agents</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost" onClick={syncWithTelnyx} disabled={syncing}>
              <RefreshCw size={16} className={syncing ? 'spin' : ''} /> {syncing ? 'Syncing...' : 'Sync'}
            </button>
            <Link to="/agents/new" className="btn btn-primary">
              <Plus size={16} /> Create Agent
            </Link>
          </div>
        </div>

        {agents.length === 0 ? (
          <div className="empty-state">
            <Bot size={48} className="empty-icon" />
            <h3>No agents yet</h3>
            <p>Create your first AI voice agent to get started.</p>
            <Link to="/agents/new" className="btn btn-primary">
              <Plus size={16} /> Create Your First Agent
            </Link>
          </div>
        ) : (
          <div className="agents-grid">
            {agents.map(agent => (
              <AgentCard key={agent.id} agent={agent} onClick={() => navigate(`/agents/${agent.id}`)} />
            ))}

            {/* Create new card */}
            <Link to="/agents/new" className="agent-card agent-card--create">
              <Plus size={32} />
              <span>Create Agent</span>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function AgentCard({ agent, onClick }) {
  const Icon = agent.type === 'inbound' ? PhoneIncoming : PhoneOutgoing;
  const statusColor = agent.status === 'active' ? '#22c55e' : '#6b7280';

  return (
    <div className="agent-card" onClick={onClick}>
      <div className="agent-card__header">
        <div className="agent-avatar">
          {agent.avatar_url ? (
            <img src={agent.avatar_url} alt={agent.name} />
          ) : (
            <Bot size={28} />
          )}
        </div>
        <div className="agent-status" style={{ background: statusColor }} title={agent.status} />
      </div>

      <div className="agent-card__body">
        <h3>{agent.name}</h3>
        <div className="agent-meta">
          <span className={`badge badge--${agent.type}`}>
            <Icon size={12} /> {agent.type}
          </span>
          <span className="badge badge--neutral">{agent.language}</span>
        </div>
      </div>

      <div className="agent-card__footer">
        <div className="agent-stat">
          <Phone size={14} />
          <span>{agent.call_count} calls</span>
        </div>
        {agent.phone_number && (
          <span className="agent-phone">{agent.phone_number}</span>
        )}
        {!agent.telnyx_agent_id && (
          <span className="badge badge--warning" title="Not synced to Telnyx">Not synced</span>
        )}
        {agent.telnyx_agent_id && agent.telnyx_synced === false && (
          <span className="badge badge--warning" title="Telnyx unreachable">Offline</span>
        )}
      </div>
    </div>
  );
}
